import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, AlertTriangle, CheckCircle2, Circle } from "lucide-react";
import { Button, Field, Spinner } from "./ui/kit";
import NovaAktivita from "./NovaAktivita";
import { NabidkaZeSablonyDialog } from "./SablonyDialogy";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import { FAZE_ORDER, FAZE_LABEL, mezikroky } from "../lib/faze";
import api, { apiError } from "../lib/api";
import Vyber from "./Vyber";

// Kroky technologie podle toho, co vrátí server (`akce` z faze.prehled_kroku):
// co jde udělat, co k tomu chybí a na co upozornit. Obrazovka nic nepočítá,
// jen zobrazí tlačítka — brány hlídá server.
//
// compact = řádek technologie v detailu případu (hlavní tlačítko + ztráta),
// jinak karta „Další krok" v detailu technologie.

export const DUVODY = {
  CENA: "Cena", KONKURENCE: "Konkurence", ODLOZIL: "Klient odložil",
  NEREAGUJE: "Klient nereaguje", ODSTOUPIL: "Klient odstoupil od objednávky", JINE: "Jiný důvod",
};

// Povinné údaje ze zaměření, které jde vyplnit rovnou u kroku „Vytvořit nabídku".
const DOPLNIT_POLE = {
  misto_instalace: { label: "Umístění v objektu", placeholder: "např. technická místnost v 1. NP, půda" },
  vykon_rozsah: { label: "Výkon / rozsah", placeholder: "např. 350 m³/h, 9 kW, 120 m² podlahy" },
};

function DoplnitUdaje({ tech, pole, udaje, setUdaje }) {
  return (
    <div className="space-y-3" data-testid={`doplnit-${tech.id}`}>
      {pole.map((k) => (
        <Field key={k} label={DOPLNIT_POLE[k]?.label || k}>
          <input id={`doplnit-${k}-${tech.id}`} className="field" value={udaje[k] || ""} placeholder={DOPLNIT_POLE[k]?.placeholder}
            onChange={(e) => setUdaje({ ...udaje, [k]: e.target.value })} data-testid={`doplnit-${k}`} />
        </Field>
      ))}
      <p className="t-meta text-muted">Adresa realizace se vyplňuje u případu, tady jde o to, kde v objektu technologie bude.</p>
    </div>
  );
}

export function Dialog({ titulek, onClose, children, testid }) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[440px] md:rounded-card rounded-t-sheet shadow-panel">
        <div className="border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <h2 className="t-sekce font-800 text-ink">{titulek}</h2>
          <button onClick={onClose} className="tap text-muted hover:text-ink" aria-label="Zavřít"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Mezikroky firmy uvnitř Poptávky (např. Zpracování poptávky) nebo Nabídky (např.
 * Finalizace, Zasmluvnění): bez brány, posouvá obchodník nebo majitel. Na kartě
 * případu jen tlačítko na další mezikrok.
 */
function Mezikroky({ tech, compact, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const faze = tech.faze || "NOVY";
  const kroky = mezikroky(faze);
  if (!["NOVY", "NABIDKA"].includes(faze) || !kroky.length) return null;
  const smi = canSee(user, ["OWNER", "OBCHODNIK"]);
  const moznosti = [{ id: null, nazev: FAZE_LABEL[faze] }, ...kroky];
  const nadpis = faze === "NOVY" ? "Kde je poptávka" : "Kde je nabídka";
  const akt = kroky.some((m) => m.id === tech.mezikrok) ? tech.mezikrok : null;
  const posun = async (id) => {
    setBusy(true);
    setChyba("");
    try {
      await api.post(`/api/technologie/${tech.id}/mezikrok`, { mezikrok: id });
      onChanged && onChanged();
    } catch (e) {
      setChyba(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  if (compact) {
    const dalsi = moznosti[moznosti.findIndex((m) => m.id === akt) + 1];
    return smi && dalsi ? (
      <button type="button" onClick={() => posun(dalsi.id)} disabled={busy} data-testid={`mezikrok-dalsi-${tech.id}`}
        className="tap md:min-h-0 h-7 px-2.5 rounded-[9px] border border-line-strong bg-white t-meta font-600 text-sec hover:text-ink disabled:opacity-60">
        → {dalsi.nazev}
      </button>
    ) : null;
  }
  return (
    <div className="pt-4 border-t border-line-softer" data-testid={`mezikroky-${tech.id}`}>
      <div className="label-up text-muted mb-2">{nadpis}</div>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={nadpis}>
        {moznosti.map((m) => {
          const on = m.id === akt;
          return (
            <button key={m.id || "nabidka"} type="button" role="radio" aria-checked={on} disabled={!smi || busy || on}
              onClick={() => posun(m.id)} data-testid={`mezikrok-${m.id || "nabidka"}`}
              className={`tap md:min-h-0 pill h-11 md:h-8 px-3.5 border t-meta font-600 transition-colors disabled:cursor-default ${
                on ? "bg-ink border-ink text-white" : "bg-white border-line-strong text-sec enabled:hover:bg-inputbg"}`}>
              {m.nazev}
            </button>
          );
        })}
      </div>
      {!smi && <div className="t-meta text-muted mt-1.5">Posouvá obchodník nebo majitel.</div>}
      {chyba && <div className="t-meta text-danger mt-1.5">{chyba}</div>}
    </div>
  );
}

export default function TechKroky({ tech, pripad, isOwner = false, compact = false, fakta = null,
  nabidkaId = null, onChanged }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const smiSablony = canSee(user, ["OWNER", "OBCHODNIK"]);   // šablony nabídek jsou hostitele
  const [busy, setBusy] = useState("");
  const [chyba, setChyba] = useState("");
  const [dialog, setDialog] = useState(null);      // {typ, ...}
  const [duvod, setDuvod] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [cilFaze, setCilFaze] = useState("");
  const akce = tech.akce || [];
  const [udaje, setUdaje] = useState({});

  const hotovo = () => { setDialog(null); setDuvod(""); setPoznamka(""); setCilFaze(""); onChanged && onChanged(); };

  const prechod = async (body, id) => {
    setBusy(id); setChyba("");
    try {
      await api.post(`/api/technologie/${tech.id}/prechod`, body);
      hotovo();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && d.varovani) {
        setDialog({ typ: "varovani", varovani: d.varovani, body });
      } else {
        setChyba(apiError(d) || e.message);
      }
    } finally { setBusy(""); }
  };

  // Doplní chybějící údaje ze zaměření a hned založí nabídku.
  const doplnitANabidka = async (a) => {
    setBusy(a.id); setChyba("");
    try {
      const body = Object.fromEntries(a.doplnit.map((k) => [k, (udaje[k] || "").trim()]));
      await api.patch(`/api/technologie/${tech.id}`, body);
      const { data } = await api.post(`/api/technologie/${tech.id}/nabidky`, {});
      nav(`/nabidky/${data.id}`);
    } catch (e) {
      setChyba(apiError(e.response?.data?.detail) || e.message);
      onChanged && onChanged();
    } finally { setBusy(""); }
  };
  const vyplneno = (a) => (a.doplnit || []).every((k) => (udaje[k] || "").trim());

  const spust = async (a) => {
    setChyba("");
    if (a.doplnit?.length && a.smi) return setDialog({ typ: "doplnit", akce: a });
    switch (a.id) {
      case "PLAN_ZAMERENI":
        return setDialog({ typ: "aktivita", oblast: "OBCHOD", typAktivity: "Zaměření", nazev: `Zaměření — ${tech.kategorie_name || ""}` });
      case "PLAN_MONTAZ":
        return setDialog({ typ: "aktivita", oblast: "SERVIS", typAktivity: "Instalace", nazev: `Montáž — ${tech.kategorie_name || ""}` });
      case "BEZ_ZAMERENI":
        return setDialog({ typ: "bez" });
      case "ZTRATIT":
        return setDialog({ typ: "ztratit" });
      case "VYTVORIT_NABIDKU":
        setBusy(a.id);
        try { const { data } = await api.post(`/api/technologie/${tech.id}/nabidky`, {}); nav(`/nabidky/${data.id}`); }
        catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(""); }
        return;
      case "OTEVRIT_NABIDKU":
        return nav(nabidkaId ? `/nabidky/${nabidkaId}` : `/technologie/${tech.id}`);
      case "PROTOKOL":
        if (!fakta) return nav(`/technologie/${tech.id}`);
        if (fakta.protokol) return nav(`/servis/zapisy/${fakta.protokol.id}`);
        if (!fakta.zarizeni_id) return setChyba("Zařízení k technologii zatím neexistuje.");
        setBusy(a.id);
        try {
          const { data } = await api.post("/api/zapisy", { zarizeni_id: fakta.zarizeni_id, typ_zapisu: "UVEDENI_DO_PROVOZU" });
          nav(`/servis/zapisy/${data.id}`);
        } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(""); }
        return;
      default:
        if (a.faze) return prechod({ faze: a.faze }, a.id);
    }
  };

  // Vedle „Vytvořit nabídku“: nabídka ze šablony hlavního zařízení.
  const tlacitko = (a, primary) => (a.id === "VYTVORIT_NABIDKU" && a.ok && smiSablony ? (
    <React.Fragment key={a.id}>
      {tlacitkoAkce(a, primary)}
      <button onClick={() => setDialog({ typ: "sablona" })} disabled={!!busy} data-testid={`krok-sablona-${tech.id}`}
        className="tap md:min-h-0 pill h-8 px-3.5 t-meta font-600 inline-flex items-center justify-center gap-1.5 border border-line-strong bg-white text-sec hover:bg-inputbg disabled:opacity-45">
        Ze šablony
      </button>
    </React.Fragment>
  ) : tlacitkoAkce(a, primary));
  const tlacitkoAkce = (a, primary) => (
    <button key={a.id} onClick={() => spust(a)} disabled={!(a.ok || (a.doplnit?.length && a.smi)) || !!busy} data-testid={`krok-${a.id}-${tech.id}`}
      title={a.chybi.length ? `Chybí: ${a.chybi.join(", ")}` : a.info || ""}
      className={`tap md:min-h-0 pill h-8 px-3.5 t-meta font-600 inline-flex items-center justify-center gap-1.5 disabled:opacity-45 ${
        primary ? "bg-ink text-white hover:bg-sec" : "border border-line-strong bg-white text-sec hover:bg-inputbg"}`}>
      {busy === a.id ? <Spinner /> : a.doplnit?.length && a.smi ? "Doplnit zaměření" : a.label}
    </button>
  );

  const hlavni = akce.filter((a) => a.id !== "ZTRATIT");
  const ztratit = akce.find((a) => a.id === "ZTRATIT");

  return (
    <>
      {compact ? (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`kroky-${tech.id}`}>
          {hlavni.slice(0, 1).map((a) => tlacitko(a, true))}
          <Mezikroky tech={tech} compact onChanged={onChanged} />
          {hlavni[0] && !hlavni[0].ok && hlavni[0].chybi.length > 0 && !(hlavni[0].doplnit?.length && hlavni[0].smi) && (
            <span className="t-meta text-muted">Chybí: {hlavni[0].chybi.join(", ")}</span>
          )}
          {chyba && <span className="t-meta text-danger basis-full">{chyba}</span>}
        </div>
      ) : (
        <div className="card p-5 md:p-6 space-y-4" data-testid="dalsi-krok">
          <div className="flex items-center justify-between gap-3">
            <h3 className="t-sekce font-700 text-ink">Další krok</h3>
            {isOwner && (
              <button onClick={() => setDialog({ typ: "oprava" })} className="t-meta text-muted hover:text-ink underline-offset-2 hover:underline"
                data-testid="opravit-fazi">Opravit fázi</button>
            )}
          </div>
          {hlavni.length === 0 && (
            <p className="t-telo text-muted">{tech.faze === "DOKONCENO" ? "Technologie je předaná, zařízení je v servisu." : "Technologie je ztracená."}</p>
          )}
          <div className="space-y-3">
            {hlavni.map((a, i) => (
              <div key={a.id} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 t-telo font-600 text-ink">
                    {a.ok ? <CheckCircle2 size={16} className="text-success shrink-0" /> : <Circle size={16} className="text-muted-2 shrink-0" />}
                    {a.label}
                  </div>
                  {a.chybi.length > 0 && <div className="t-meta text-danger ml-6">Chybí: {a.chybi.join(", ")}</div>}
                  {a.varovani.length > 0 && <div className="t-meta text-[#b7791f] ml-6">Pozor: {a.varovani.join(", ")}</div>}
                  {a.info && !a.chybi.length && <div className="t-meta text-muted ml-6">{a.info}</div>}
                  {a.doplnit?.length > 0 && a.smi && (
                    <div className="ml-6 mt-3 max-w-[460px]">
                      <DoplnitUdaje tech={tech} pole={a.doplnit} udaje={udaje} setUdaje={setUdaje} />
                    </div>
                  )}
                </div>
                <div className="md:shrink-0 md:self-start">
                  {a.doplnit?.length > 0 && a.smi ? (
                    <Button onClick={() => doplnitANabidka(a)} disabled={!vyplneno(a) || !!busy} data-testid={`krok-doplnit-${tech.id}`}>
                      {busy === a.id ? <Spinner /> : "Uložit a vytvořit nabídku"}
                    </Button>
                  ) : <span className="flex flex-wrap gap-1.5">{tlacitko(a, i === 0 || a.ok)}</span>}
                </div>
              </div>
            ))}
          </div>
          <Mezikroky tech={tech} onChanged={onChanged} />
          {chyba && <div className="t-meta text-danger">{chyba}</div>}
        </div>
      )}
      {compact && ztratit && (
        <button onClick={() => spust(ztratit)} data-testid={`tech-ztratit-${tech.id}`} title="Označit jako ztracenou"
          className="tap md:min-h-0 h-7 px-2 rounded-[9px] border border-line-strong bg-white t-meta text-muted-2 hover:text-danger hover:border-danger/40">
          Ztraceno
        </button>
      )}
      {!compact && ztratit && (
        <button onClick={() => spust(ztratit)} data-testid={`tech-ztratit-${tech.id}`}
          className="t-meta text-muted hover:text-danger">Označit technologii jako ztracenou</button>
      )}

      {/* Okna přes celou obrazovku — ne uvnitř náhledu případu (posuvný panel by je ořízl). */}
      {dialog && createPortal(<>
      {dialog?.typ === "aktivita" && (
        <NovaAktivita onClose={() => setDialog(null)} onSaved={hotovo}
          defaultClientId={pripad?.client_id} pripadId={pripad?.id || tech.obchodni_pripad_id}
          technologieId={tech.id} defaultOblast={dialog.oblast} defaultTyp={dialog.typAktivity} defaultNazev={dialog.nazev} />
      )}

      {dialog?.typ === "sablona" && <NabidkaZeSablonyDialog tech={tech} onClose={() => setDialog(null)} />}

      {dialog?.typ === "doplnit" && (
        <Dialog titulek="Doplnit zaměření" onClose={() => setDialog(null)} testid="dialog-doplnit">
          <DoplnitUdaje tech={tech} pole={dialog.akce.doplnit} udaje={udaje} setUdaje={setUdaje} />
          {chyba && <div className="t-meta text-danger">{chyba}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDialog(null)}>Zpět</Button>
            <Button onClick={() => doplnitANabidka(dialog.akce)} disabled={!vyplneno(dialog.akce) || !!busy} data-testid="doplnit-potvrdit">
              {busy ? <Spinner /> : "Uložit a vytvořit nabídku"}
            </Button>
          </div>
        </Dialog>
      )}

      {dialog?.typ === "ztratit" && (
        <Dialog titulek="Technologie je ztracená" onClose={() => setDialog(null)} testid="dialog-ztratit">
          <Field label="Důvod">
            <Vyber id="ztrata-duvod" className="field" value={duvod} onChange={(e) => setDuvod(e.target.value)} data-testid="ztrata-duvod">
              <option value="">Vyber důvod…</option>
              {Object.entries(DUVODY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Vyber>
          </Field>
          <Field label="Poznámka (nepovinná)">
            <input id="ztrata-poznamka" className="field" value={poznamka} onChange={(e) => setPoznamka(e.target.value)} placeholder="např. konkurence levnější o 15 %" />
          </Field>
          <p className="t-meta text-muted">Otevřené úkoly technologie se zruší a rozpracovaná nabídka se označí jako zamítnutá.</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDialog(null)}>Zpět</Button>
            <Button onClick={() => prechod({ faze: "ZTRACENO", duvod, poznamka }, "ZTRATIT")} disabled={!duvod || !!busy} data-testid="ztrata-potvrdit">
              {busy ? <Spinner /> : "Označit jako ztracenou"}
            </Button>
          </div>
        </Dialog>
      )}

      {dialog?.typ === "bez" && (
        <Dialog titulek="Bez zaměření" onClose={() => setDialog(null)} testid="dialog-bez-zamereni">
          <Field label="Proč se nezaměřuje">
            <textarea id="bez-zamereni-duvod" className="field min-h-[80px] py-2" value={poznamka} onChange={(e) => setPoznamka(e.target.value)}
              placeholder="např. klimatizace podle projektu, rozměry od klienta" data-testid="bez-zamereni-duvod" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDialog(null)}>Zpět</Button>
            <Button disabled={poznamka.trim().length < 3 || !!busy} data-testid="bez-zamereni-potvrdit" onClick={async () => {
              setBusy("BEZ"); setChyba("");
              try { await api.post(`/api/technologie/${tech.id}/bez-zamereni`, { duvod: poznamka }); hotovo(); }
              catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); setDialog(null); }
              finally { setBusy(""); }
            }}>{busy ? <Spinner /> : "Uložit"}</Button>
          </div>
        </Dialog>
      )}

      {dialog?.typ === "varovani" && (
        <Dialog titulek="Opravdu pokračovat?" onClose={() => setDialog(null)} testid="dialog-varovani">
          <div className="flex gap-2 t-telo text-ink">
            <AlertTriangle size={18} className="text-[#b7791f] shrink-0 mt-0.5" />
            <span>{dialog.varovani.map((v) => v.charAt(0).toUpperCase() + v.slice(1)).join(". ")}.</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDialog(null)}>Zpět</Button>
            <Button onClick={() => prechod({ ...dialog.body, potvrzuji_varovani: true }, "VAROVANI")} disabled={!!busy} data-testid="varovani-potvrdit">
              {busy ? <Spinner /> : "Pokračovat"}
            </Button>
          </div>
        </Dialog>
      )}

      {dialog?.typ === "oprava" && (
        <Dialog titulek="Opravit fázi" onClose={() => setDialog(null)} testid="dialog-oprava">
          <p className="t-meta text-muted">Oprava přeskočí kontrolu kroků. Použij ji jen na chybu v datech; zapíše se do historie.</p>
          <Field label="Nová fáze">
            <Vyber id="oprava-faze" className="field" value={cilFaze} onChange={(e) => setCilFaze(e.target.value)} data-testid="oprava-faze">
              <option value="">Vyber fázi…</option>
              {FAZE_ORDER.filter((f) => f !== tech.faze).map((f) => <option key={f} value={f}>{FAZE_LABEL[f]}</option>)}
            </Vyber>
          </Field>
          <Field label="Proč">
            <input id="oprava-poznamka" className="field" value={poznamka} onChange={(e) => setPoznamka(e.target.value)} data-testid="oprava-poznamka" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDialog(null)}>Zpět</Button>
            <Button disabled={!cilFaze || !poznamka.trim() || !!busy} data-testid="oprava-potvrdit"
              onClick={() => prechod({ faze: cilFaze, oprava: true, poznamka, duvod: cilFaze === "ZTRACENO" ? "JINE" : null }, "OPRAVA")}>
              {busy ? <Spinner /> : "Opravit"}
            </Button>
          </div>
        </Dialog>
      )}
      </>, document.body)}
    </>
  );
}
